import React, { useMemo } from 'react';
import MuiTooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { getDuration } from '@composite/calendar/utils/get-duration';
import { getTaskInterval } from '@composite/calendar/utils/get-task-interval';
import { WorkOrder } from '@models';
import {
  useTooltipStyles,
  useTooltipWrapperStyles,
} from './task-tooltip-wrapper-styles';

const getWorkOrderInfo = (w: WorkOrder) => {
  if (w?.currentState?.code === 'PRE') {
    return {
      workOrderId: w?.parentWorkOrder?.workOrderId,
      operationNumber: w?.parentWorkOrder?.operationNumber,
    };
  }
  return {
    workOrderId: w?.workOrderId,
    operationNumber: w?.operationNumber,
  };
};

export const TaskTooltip = ({
  workOrder,
  children,
}: {
  workOrder: WorkOrder;
  children: React.ReactNode;
}) => {
  const classes = useTooltipStyles();
  const tooltipWrapper = useTooltipWrapperStyles();
  const wrappedChildren = useMemo(
    () =>
      React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child) => React.cloneElement(child))[0],
    [children]
  );

  const { operationNumber, workOrderId } = getWorkOrderInfo(workOrder);

  return (
    <MuiTooltip
      PopperProps={{
        disablePortal: false,
      }}
      interactive={false}
      classes={tooltipWrapper}
      title={
        <Typography component="div">
          <div className={classes.tooltip}>
            {workOrder.locked && (
              <div className={classes.tooltipContent}>
                <b>Uzamknutá</b>
              </div>
            )}

            {workOrderId !== undefined && (
              <div className={classes.tooltipContent}>
                Číslo zákazky: <b># {workOrderId}</b>
              </div>
            )}
            {operationNumber && (
              <div className={classes.tooltipContent}>
                Číslo operácie: <b># {operationNumber}</b>
              </div>
            )}
            <div className={classes.tooltipContent}>
              Dohodnuté so zákazníkom:
              <b> {workOrder.customerAgreed ? 'Áno' : 'Nie'} </b>
            </div>

            {workOrder.workOrderType && (
              <div className={classes.tooltipContent}>
                Druh práce:
                <b> {workOrder.workOrderType.name} </b>
              </div>
            )}

            <div className={classes.tooltipContent}>
              Stav:{' '}
              <div className={classes.content}>
                <div
                  className={classes.bullet}
                  style={{
                    backgroundColor: workOrder.currentState?.color,
                  }}
                />
                <b>{workOrder.currentState?.name}</b>
              </div>
            </div>
            {workOrder.dueDate && (
              <div className={classes.tooltipContent}>
                Realizácia do:
                <b>
                  {new Date(workOrder.dueDate).toLocaleDateString('sk-SK')}{' '}
                </b>
              </div>
            )}
            {workOrder.address?.city && (
              <div className={classes.tooltipContent}>
                Mesto:
                <b>{workOrder.address.city}</b>
              </div>
            )}
            {workOrder.address?.street && (
              <div className={classes.tooltipContent}>
                Ulica:
                <b>{workOrder.address.street}</b>
              </div>
            )}
            {workOrder.address?.houseNumber && (
              <div className={classes.tooltipContent}>
                Číslo popisné:
                <b>{workOrder.address.houseNumber}</b>
              </div>
            )}
            {workOrder.road && (
              <div className={classes.tooltipContent}>
                Cesta:
                <span className={classes.overflowText}>{workOrder.road}</span>
              </div>
            )}
            {(workOrder.technicalObjects ?? []).length > 0 && (
              <div className={classes.tooltipContent}>
                Počet TO:
                <b>{(workOrder.technicalObjects ?? []).length}</b>
              </div>
            )}
            {!!workOrder.duration &&
              !!workOrder.startTime &&
              !!workOrder.endTime && (
                <div className={classes.tooltipContent}>
                  Trvanie:{' '}
                  <b>
                    {getTaskInterval(workOrder.startTime, workOrder.endTime)} (
                    {getDuration(workOrder.duration)})
                  </b>
                </div>
              )}
            {!!workOrder.travelPrevious && (
              <div className={classes.tooltipContent}>
                Prejazd: <b>{getDuration(workOrder.travelPrevious)}</b>
              </div>
            )}
          </div>
        </Typography>
      }
    >
      {wrappedChildren}
    </MuiTooltip>
  );
};
